import PageMeta from "@/components/PageMeta";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Check,
  Layers,
  Package,
  Scissors,
  Sparkles,
  SquareStack,
  Tag,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import SectionFadeIn from "@/components/SectionFadeIn";
import { getRouteState } from "@/lib/navigation";
import {
  calculateStickerPrice,
  fetchStickerConfig,
  formatGBP,
  getFallbackStickerConfig,
} from "@/lib/stickerPricing";
import { StickerConfigurator } from "@/components/stickers/StickerConfigurator";

/* ── Format definitions ── */

const FORMATS = [
  { id: "die-cut", label: "Die-Cut Stickers", short: "Contour-cut to your artwork shape", icon: Scissors, image: "https://images.unsplash.com/photo-1710153601719-282f8064b3fe?w=600&q=80" },
  { id: "kiss-cut", label: "Kiss-Cut Stickers", short: "Easy-peel on backing sheet", icon: Layers, image: "https://images.unsplash.com/photo-1635405074683-96d6921a2a68?w=600&q=80" },
  { id: "sticker-sheets", label: "Sticker Sheets", short: "Multiple designs on one sheet", icon: SquareStack, image: "https://images.pexels.com/photos/7563593/pexels-photo-7563593.jpeg?w=600&q=80" },
  { id: "product-labels", label: "Product Labels", short: "Bottles, jars, packaging, retail", icon: Tag, image: "https://images.unsplash.com/photo-1668419911929-70a6cfa7f9f1?w=600&q=80" },
  { id: "window-decals", label: "Window Decals", short: "Glass branding and display", icon: Package, image: "https://images.unsplash.com/photo-1693147726994-b1a5480e35f0?w=600&q=80" },
];

const PRICE_GUIDE = [
  { format: "die-cut", label: "Die-Cut 50\u00d750mm", area: 25, mat: "white-vinyl", qty: 100, desc: "White vinyl, matte, \u00d7100" },
  { format: "die-cut", label: "Die-Cut 100\u00d7100mm", area: 100, mat: "white-vinyl", qty: 100, desc: "White vinyl, matte, \u00d7100" },
  { format: "kiss-cut", label: "Kiss-Cut 70\u00d770mm", area: 49, mat: "white-vinyl", qty: 250, desc: "White vinyl, gloss, \u00d7250" },
  { format: "product-labels", label: "Product Label 80\u00d760mm", area: 48, mat: "kraft", qty: 500, desc: "Kraft paper, matte, \u00d7500" },
  { format: "sticker-sheets", label: "Sticker Sheet A5", area: 310.8, mat: "white-vinyl", qty: 25, desc: "White vinyl, matte, \u00d725 sheets" },
  { format: "window-decals", label: "Window Decal 50\u00d730cm", area: 1500, mat: "adhesive-vinyl", qty: 1, desc: "Adhesive vinyl, \u00d71" },
];

/* ── Main Page ── */

export default function StickersLabelsPage() {
  const location = useLocation();

  /* Pricing config from API */
  const [pricingConfig, setPricingConfig] = useState(getFallbackStickerConfig());
  useEffect(() => {
    // `fetchStickerConfig` is a stable module-level import and `setPricingConfig`
    // is a useState setter (stable by React contract). Neither belongs in deps.
    let cancelled = false;
    fetchStickerConfig().then((cfg) => { if (!cancelled) setPricingConfig(cfg); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── State ── */
  const [selectedFormat, setSelectedFormat] = useState(null);

  const formatDef = FORMATS.find((f) => f.id === selectedFormat);

  const calc = useCallback(
    (opts) => calculateStickerPrice(opts, pricingConfig),
    [pricingConfig]
  );

  /* Hero starting price */
  const heroStartingPrice = useMemo(
    () => calc({ formatId: "die-cut", areaSqcm: 25, materialId: "white-vinyl", quantity: 100 }).unitPrice,
    [calc]
  );

  /* Price guide */
  const guidePrices = useMemo(
    () => PRICE_GUIDE.map((g) => calc({ formatId: g.format, areaSqcm: g.area, materialId: g.mat, quantity: g.qty })),
    [calc]
  );

  const handleFormatSelect = (fmtId) => {
    setSelectedFormat(fmtId);
    setTimeout(() => document.getElementById("sticker-configurator-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  return (
    <div className="py-16 md:py-24 pb-28 md:pb-24" data-testid="stickers-labels-page">
      <PageMeta
        title="Stickers & Labels"
        description="Custom stickers, labels, and decals. Die-cut, kiss-cut, sticker sheets, product labels, and window decals. Configure your order and get a live price estimate."
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* ── Hero ── */}
        <SectionFadeIn>
          <section
            className="rounded-sm border border-brand-border bg-[radial-gradient(circle_at_top,_rgba(203,160,82,0.12),_transparent_32%),linear-gradient(135deg,rgba(255,255,255,0.03),rgba(10,13,20,0.96))] px-6 py-14 md:px-10 md:py-16"
            data-testid="stickers-hero"
          >
            <div className="max-w-3xl">
              <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-brand-gold">
                Stickers, Labels &amp; Decals
              </p>
              <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-brand-text" data-testid="stickers-heading">
                Choose a format, configure it, get a price
              </h1>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-brand-text-secondary" data-testid="stickers-hero-copy">
                Die-cut stickers, kiss-cut handouts, product labels, sticker sheets, and window decals
                &mdash; all printed on a Roland TrueVIS VG-640 with contour cutting and optional lamination.
              </p>
              <div className="mt-5 inline-flex items-center gap-3 rounded-sm border border-brand-gold/25 bg-brand-gold/[0.06] px-5 py-3" data-testid="stickers-hero-price">
                <Tag size={16} className="text-brand-gold shrink-0" />
                <span className="text-[14px] font-semibold text-brand-text">
                  Custom stickers from <span className="text-brand-gold">{formatGBP(heroStartingPrice)}</span>/unit
                </span>
                <span className="text-[11px] text-brand-text-secondary/50">at 100+</span>
              </div>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="stickers-service-tags">
                {["Die-Cut", "Kiss-Cut", "Sticker Sheets", "Product Labels", "Window Decals", "Lamination Available"].map((tag) => (
                  <span key={tag} className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[10px] uppercase tracking-[0.14em] text-brand-text-secondary/70">{tag}</span>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="stickers-hero-ctas">
                <button type="button" onClick={() => document.getElementById("format-selector")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 rounded-sm bg-brand-gold px-7 py-3.5 text-[13px] font-semibold tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover" data-testid="stickers-hero-configure-cta">
                  Choose Your Format <ArrowRight size={15} />
                </button>
                <Link to="/request-quote" state={getRouteState(location)} className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-7 py-3.5 text-[13px] font-medium tracking-wide text-brand-text transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold" data-testid="stickers-hero-custom-cta">
                  Need something custom? <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Format Selector ── */}
        <SectionFadeIn>
          <section id="format-selector" className="mt-16" data-testid="stickers-format-section">
            <div className="max-w-2xl mb-8">
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Step 1 of 4</p>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-brand-text">What type of sticker or label?</h2>
              <p className="mt-2 text-[13px] text-brand-text-secondary/60">Pick a format and the options below will adapt to that product type.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="format-cards-grid">
              {FORMATS.map((fmt, index) => {
                const isActive = selectedFormat === fmt.id;
                return (
                  <SectionFadeIn key={fmt.id} delay={index * 50}>
                    <button type="button" onClick={() => handleFormatSelect(fmt.id)} className={`group relative h-full w-full rounded-sm border text-left transition-all duration-300 hover:-translate-y-0.5 overflow-hidden ${isActive ? "border-brand-gold ring-1 ring-brand-gold/30 shadow-[0_0_16px_rgba(203,160,82,0.1)]" : "border-white/10 hover:border-white/25"}`} data-testid={`format-card-${fmt.id}`}>
                      <div className="aspect-[16/10] overflow-hidden bg-brand-bg relative">
                        <img src={fmt.image} alt={fmt.label} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] opacity-70" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/40 to-transparent" />
                        {isActive && (
                          <div className="absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full bg-brand-gold">
                            <Check size={13} className="text-brand-bg" />
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className={`mb-2 ${isActive ? "text-brand-gold" : "text-brand-text-secondary/60"}`}><fmt.icon size={18} /></div>
                        <p className={`text-[14px] font-semibold ${isActive ? "text-brand-gold" : "text-brand-text"}`}>{fmt.label}</p>
                        <p className={`mt-1 text-[12px] leading-relaxed ${isActive ? "text-brand-gold/60" : "text-brand-text-secondary/50"}`}>{fmt.short}</p>
                      </div>
                    </button>
                  </SectionFadeIn>
                );
              })}
            </div>
          </section>
        </SectionFadeIn>

        {/* ── Quick Price Guide ── */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="sticker-price-guide">
            <div className="max-w-2xl mb-8">
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Quick price guide</p>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Representative sticker &amp; label prices</h2>
              <p className="mt-2 text-[13px] text-brand-text-secondary/60">Per-unit prices at the listed quantity. Bulk discounts apply at 250+, 500+, and 1,000+ units.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="sticker-price-guide-table">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    <th className="py-3 pr-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60">Product</th>
                    <th className="py-3 pr-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60 hidden sm:table-cell">Spec</th>
                    <th className="py-3 text-right text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60">Per unit</th>
                    <th className="py-3 text-right text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/60">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICE_GUIDE.map((ex, i) => (
                    <tr key={ex.label} className="border-b border-white/[0.04] last:border-0">
                      <td className="py-3.5 pr-4">
                        <span className="text-[13px] font-medium text-brand-text">{ex.label}</span>
                        <span className="block sm:hidden mt-0.5 text-[11px] text-brand-text-secondary/50">{ex.desc}</span>
                      </td>
                      <td className="py-3.5 pr-4 text-[12px] text-brand-text-secondary/55 hidden sm:table-cell">{ex.desc}</td>
                      <td className="py-3.5 text-right">
                        <span className="text-[14px] font-bold text-brand-gold" data-testid={`guide-unit-price-${i}`}>{formatGBP(guidePrices[i]?.unitPrice || 0)}</span>
                      </td>
                      <td className="py-3.5 text-right">
                        <span className="text-[13px] font-semibold text-brand-text" data-testid={`guide-total-price-${i}`}>{formatGBP(guidePrices[i]?.totalPrice || 0)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-[11px] text-brand-text-secondary/40">Prices are indicative and include print. Final quote confirmed after artwork review.</p>
          </section>
        </SectionFadeIn>

        {/* ── Configurator ── */}
        <div id="sticker-configurator-anchor">
          {selectedFormat && (
            <SectionFadeIn>
              <StickerConfigurator
                selectedFormat={selectedFormat}
                formatDef={formatDef}
                pricingConfig={pricingConfig}
              />
            </SectionFadeIn>
          )}
        </div>

        {/* ── Custom order fallback ── */}
        <SectionFadeIn>
          <section className="mt-16 rounded-sm border border-white/[0.05] bg-brand-surface/40 px-6 py-12 md:px-8" data-testid="stickers-final-cta-section">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 text-brand-gold mb-2">
                  <Sparkles size={16} />
                  <span className="text-[11px] uppercase tracking-[0.16em] font-semibold">Need something different?</span>
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Custom sticker jobs welcome</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-brand-text-secondary max-w-2xl">
                  Unusual shapes, special materials, large runs, or anything that doesn't fit the configurator above &mdash; send us the brief and we'll quote it properly.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link to="/request-quote?type=Business%20Signage&description=Custom%20sticker%20%2F%20label%20enquiry." state={getRouteState(location)} className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-gold px-8 py-3.5 text-[13px] font-semibold tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover" data-testid="stickers-custom-quote-cta">
                  Request Custom Quote <ArrowRight size={15} />
                </Link>
                <Link to="/request-quote" state={getRouteState(location)} className="inline-flex items-center justify-center gap-2 rounded-sm border border-brand-gold/50 px-8 py-3.5 text-[13px] font-medium tracking-wide text-brand-gold transition-colors duration-300 hover:bg-brand-gold/10" data-testid="stickers-upload-artwork-cta">
                  Upload Artwork &amp; Brief <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </section>
        </SectionFadeIn>
      </div>
    </div>
  );
}
